<script setup>
import { onMounted, ref } from 'vue'
import { useAuthStore } from '../stores/auth.js'
import { updateMyProfileRequest } from '../services/userService.js'

const authStore = useAuthStore()

const fullName = ref('')
const email = ref('')
const phone = ref('')
const loading = ref(false)
const success = ref('')
const error = ref('')

const parseApiError = (err, fallback) => {
  const detail = err?.response?.data?.detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail) && detail.length) {
    return detail.map((item) => item?.msg).filter(Boolean).join(', ') || fallback
  }
  return fallback
}

onMounted(() => {
  fullName.value = String(authStore.user?.full_name ?? '')
  email.value = String(authStore.user?.email ?? '')
  phone.value = String(authStore.user?.phone ?? '')
})

const saveProfile = async () => {
  loading.value = true
  success.value = ''
  error.value = ''

  try {
    const payload = {
      full_name: fullName.value.trim(),
      email: email.value.trim(),
      phone: phone.value.trim(),
    }

    const updated = await updateMyProfileRequest(payload)

    if (authStore.user) {
      authStore.user = {
        ...authStore.user,
        full_name: String(updated?.full_name ?? payload.full_name),
        email: String(updated?.email ?? payload.email),
        phone: String(updated?.phone ?? payload.phone),
      }
    }

    success.value = 'Profile updated.'
  } catch (err) {
    error.value = parseApiError(err, 'Failed to update profile.')
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <section class="settings-wrap">
    <h1>Profile</h1>

    <form class="panel form" @submit.prevent="saveProfile">
      <label class="form-label">
        Full name
        <input v-model="fullName" class="form-input" type="text" autocomplete="name" />
      </label>

      <label class="form-label">
        Email
        <input v-model="email" class="form-input" type="email" autocomplete="email" />
      </label>

      <label class="form-label">
        Phone
        <input v-model="phone" class="form-input" type="tel" autocomplete="tel" />
      </label>

      <button class="btn btn-primary" type="submit" :disabled="loading">
        {{ loading ? 'Saving...' : 'Save' }}
      </button>

      <p v-if="success" class="success">{{ success }}</p>
      <p v-if="error" class="error">{{ error }}</p>
    </form>
  </section>
</template>

<style scoped>
.settings-wrap {
  max-width: 720px;
  margin: 0 auto;
  padding: 2rem;
  display: grid;
  gap: 1rem;
}

.form {
  display: grid;
  gap: 0.75rem;
}

.form-label {
  display: grid;
  gap: 0.35rem;
  color: #111827;
}

.form-input {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.5rem 0.6rem;
}

.success {
  color: #166534;
  background: #dcfce7;
  border-radius: 6px;
  padding: 0.5rem;
}

.error {
  color: #991b1b;
  background: #fee2e2;
  border-radius: 6px;
  padding: 0.5rem;
}
</style>

